import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Form as BootstrapForm, Button, Row, Col, InputGroup } from 'react-bootstrap';
import { Form, Group, Member } from '../../types';
import { fetchFormById } from '../../helpers';

type FormCreationProps = {
  form?: Form | null;
  saveForm?: (form: Form) => Promise<Form | null>;
  updateForm?: (formId: string, form: Form) => Promise<Form | null>;
};

const FormCreation = ({ saveForm, updateForm }: FormCreationProps) => {
  const navigate = useNavigate();
  const { formId } = useParams<{ formId: string }>();

  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [allowMultipleGroups, setAllowMultipleGroups] = useState<boolean>(false);
  const [allowEditGroupTitles, setAllowEditGroupTitles] = useState<boolean>(false);
  const [showGroupMembers, setShowGroupMembers] = useState<boolean>(false);
  const [endOption, setEndOption] = useState<string>('noDate');
  const [endDate, setEndDate] = useState<string | null>('');
  const [groups, setGroups] = useState<Group[]>([
    { id: '', name: 'Group 1', maxStudents: 1, members: [] },
  ]);
  const API_URL = process.env.REACT_APP_API_URL;
  const [totalRosterMembers, setTotalRosterMembers] = useState<number>(0);

  // Function to add a new group
  const handleAddGroup = () => {
    const lastMaxStudents = groups[groups.length - 1]?.maxStudents || 1;
    const newGroupNumber = groups.length + 1;
    setGroups([
        ...groups,
        {
          id: '',
          name: `Group ${newGroupNumber}`,
          maxStudents: lastMaxStudents,
          members: [],
        },
      ]);
  };

  // Function to update group details
  const handleUpdateGroup = (index: number, field: keyof Group, value: string | number) => {
    const updatedGroups = groups.map((group, i) =>
      i === index ? { ...group, [field]: value } : group
    );
    setGroups(updatedGroups);
  };

  const [removedGroups, setRemovedGroups] = useState<string[]>([]); 

  // Function to remove a group
  const handleRemoveGroup = (index: number) => {
    const groupToRemove = groups[index];
  
    if (groupToRemove.id) {
    setRemovedGroups([...removedGroups, groupToRemove.id]); // Store removed group's ID
    }
    setGroups(groups.filter((_, i) => i !== index));
  };

  // Function to handle cancel button click
  const handleCancel = () => {
    const confirmLeave = window.confirm(
      'You have unsaved changes. Are you sure you want to leave this page without saving?'
    );
    if (confirmLeave) {
      navigate('/'); // Navigate back to the main page
    }
  };

  // Load the form data if in edit mode
  useEffect(() => {
    const loadForm = async () => {
      if (formId) {
        try {
          const fetchedForm = await fetchFormById(formId);
          if (fetchedForm) {
            //console.log("Form data on load:", fetchedForm);  // Debugging line to confirm form data on load
            setTitle(fetchedForm.title || '');
            setDescription(fetchedForm.description || '');
            setAllowMultipleGroups(fetchedForm.allowMultipleGroups || false);
            setAllowEditGroupTitles(fetchedForm.allowEditGroupTitles || false);
            setShowGroupMembers(fetchedForm.showGroupMembers !== undefined ? fetchedForm.showGroupMembers : false);
            setEndOption(fetchedForm.endOption || 'noDate');
            setEndDate(fetchedForm.endDate || null);
            // Check if groups exist before setting state
            setGroups(fetchedForm.groups && fetchedForm.groups.length > 0 ? fetchedForm.groups : [{ id: '', name: 'Group 1', maxStudents: 1, members: [] }]);
            //console.log('Loaded showGroupMembers:', fetchedForm.showGroupMembers);
          }
        } catch (err) {
          console.error("failed to load form data: ", err);
        }
      }
    };
    loadForm()
  }, [formId]);


  // Function to handle form submission
  const handleSubmit = async () => {
    const courseGuid = sessionStorage.getItem('courseGuid') as string;
    const adjustedEndDate = endDate ? new Date(endDate).toISOString() : null; // Convert to full UTC format
    
    if (!courseGuid) {
      alert("Course information is missing.")
    }

    const filteredGroups = groups
      .map(({ id, ...group }) => ({
        ...group,
        id: id ? id : '',  // Keep existing ID, omit for new groups
      }))
      

    const newForm: Form = {
      title,
      description,
      allowMultipleGroups,
      allowEditGroupTitles,
      showGroupMembers,
      endOption,
      endDate: endOption === 'date' ? adjustedEndDate : null,
      groups: filteredGroups,
      courseGuid,
    };
    console.log("Submitting form data:", newForm);
    try {
      if (formId && updateForm) {
        await updateForm(formId, newForm);
        
        // **Delete Removed Groups from Backend**
        await Promise.all(
          removedGroups.map(async (groupId) => {
            await fetch(`${API_URL}/api/groups/${groupId}`, {
              method: "DELETE",
              credentials: "include",
            });
          })
        );

      } else if (saveForm) {
        await saveForm(newForm);
      }

      navigate('/');
    } catch (err) {
      console.error('Error saving form:', err);
    }
  };

  // Calculate total roster members and group spots
  useEffect(() => {
    const fetchRosterData = async () => {
      try {
        const response = await fetch(`${API_URL}/api/roster`, { credentials: "include" });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        //console.log("total members in roster", data.members.length)
        const studentMembers = data.members.filter((member: Member) => member.role !== "Instructor");
        //console.log("student Members:", studentMembers)
        setTotalRosterMembers(studentMembers.length);
      } catch (err) {
        console.error("Error fetching roster data:", err);
      }
    };
    fetchRosterData();
  }, []);

  const totalGroupSpots = groups.reduce((total, group) => total + group.maxStudents, 0);

  return (
    <Container>
      <h4>{formId ? 'Editing Sign-Up Form' : 'Creating New Sign-Up Form'}</h4>
      <BootstrapForm>
        {/* GENERAL FORM DETAILS */}
        <h5 className="mt-4">1. Basic details:</h5>
        <BootstrapForm.Group controlId="formTitle" className="mb-3">
          <BootstrapForm.Label>Title</BootstrapForm.Label>
          <BootstrapForm.Control
            type="text"
            placeholder="Name your new Sign-up sheet"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </BootstrapForm.Group>

        <BootstrapForm.Group controlId="formDescription" className="mb-3">
          <BootstrapForm.Label>Description/Instructions</BootstrapForm.Label>
          <BootstrapForm.Control
            as="textarea"
            rows={3}
            placeholder="Sign-up sheet description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </BootstrapForm.Group>

        {/* Create Groups Section */}
        <h5 className="mt-4">2. Create Groups:</h5>
        <p className="text-muted">{formId ? 'Changing groups while students are assigned may cause unintended results. If students are enrolled in this form, consider removing all users from groups first in the form submissions page.' : 'Create your sign-up groups and set Max Group Size.'}</p>
        <p className="text-muted"><span className="fw-bold">Class Roster size:</span> {totalRosterMembers} students</p>
        {totalRosterMembers > totalGroupSpots && (
          <p className="text-danger fw-bold">
            Attention: You need {totalRosterMembers - totalGroupSpots} more spot(s) to allow all students to join a group.
          </p>
        )}
        {groups.map((group, index) => (
          <InputGroup key={index} className="mb-2">
            <InputGroup.Text>Group Name:</InputGroup.Text>
            <BootstrapForm.Control
              type="text"
              placeholder={`Group ${index + 1}`}
              value={group.name}
              onChange={(e) => handleUpdateGroup(index, 'name', e.target.value)}
            />
            <InputGroup.Text>Max group size:</InputGroup.Text>
            <BootstrapForm.Control
              type="number"
              min="1"
              value={group.maxStudents}
              onChange={(e) => handleUpdateGroup(index, 'maxStudents', parseInt(e.target.value) || 1)}
              style={{ width: '100px' }}
            />
            <Button variant="danger" onClick={() => handleRemoveGroup(index)}>Remove</Button>
          </InputGroup>
        ))}
        <Button variant="secondary" onClick={handleAddGroup} className="mb-3">
          <span className="fw-bold">+</span> New Group
        </Button>
        
        {/* Group Member Visibility */}
        <BootstrapForm.Group controlId="showGroupMembers" className="mb-3">
          <BootstrapForm.Check
            type="checkbox"
            label="Allow students to see group members"
            checked={showGroupMembers}
            onChange={(e) => setShowGroupMembers(e.target.checked)}
          />
        </BootstrapForm.Group>

        {/* Allow multiple groups and edit group titles options */}
        <BootstrapForm.Group controlId="allowMultipleGroups" className="mb-3">
          <BootstrapForm.Check
            type="checkbox"
            label="Allow students to join multiple groups"
            checked={allowMultipleGroups}
            onChange={(e) => setAllowMultipleGroups(e.target.checked)}
          />
        </BootstrapForm.Group>

        <BootstrapForm.Group controlId="allowEditGroupTitles" className="mb-3">
          <BootstrapForm.Check
            type="checkbox"
            label="Allow students to edit group titles"
            checked={allowEditGroupTitles}
            onChange={(e) => setAllowEditGroupTitles(e.target.checked)}
          />
        </BootstrapForm.Group>

        {/* Choose How to End Sign-Up */}
        <h5 className="mt-4">3. Closing the sign-up sheet:</h5>
        <BootstrapForm.Group controlId="endOption" className="mb-3">
          <BootstrapForm.Label>Sign-up closure preferences:</BootstrapForm.Label>
          <BootstrapForm.Check
            type="radio"
            label="Leave form open"
            name="endOption"
            checked={endOption === 'noDate'}
            onChange={() => {
              setEndOption('noDate');
              setEndDate(null);
            }}          />
          <BootstrapForm.Check
            type="radio"
            label="Close form by date"
            name="endOption"
            checked={endOption === 'date'}
            onChange={() => setEndOption('date')}
          />
          {endOption === 'date' && (
            <Row className="mt-2 ">
              <Col xs={12} md={6}>
                <BootstrapForm.Group controlId="endDate">
                  <BootstrapForm.Label>End Date</BootstrapForm.Label>
                  <div></div>
                   <small className="text-muted">
                    Select a date and time for when the form will close. 
                  </small>
                  <div></div>
                  <small className="text-muted">
                    Time selected is based on your local time and converted for students. 
                  </small>
                  <BootstrapForm.Control
                    type="datetime-local"
                    value={endDate ? endDate.replace("Z", "") : ""}
                    onChange={(e) => {
                      const selectedDate = e.target.value; // Keep the selected value as-is
                      setEndDate(selectedDate); // Store it without UTC conversion
                    }}
                  />
                </BootstrapForm.Group>
              </Col>
            </Row>
          )}
        </BootstrapForm.Group>

        {/* Submit and Cancel Buttons */}
        <br />
        <Row className="mt-3 justify-content-center">
          <Col xs="auto">
            <Button variant="primary" onClick={handleSubmit}>
              {formId ? 'Update Form' : 'Create Form'}
            </Button>
          </Col>
          <Col xs="auto">
            <Button variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
          </Col>
        </Row>
      </BootstrapForm>
    </Container>
  );
};

export default FormCreation;
